import { Trophy, FileText, Split, DollarSign, Users, Building, ChevronLeft, ChevronRight, TrendingUp, BarChart3, ChevronDown, ChevronRight as ChevronRightSmall, Megaphone, Ticket, Lightbulb, HeadphonesIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useState } from "react";

// Import additional icons
import { Settings, UserCog, FileSignature, Shield, Wallet, AlertCircle } from "lucide-react";

// Define the navigation structure
const getNavigationStructure = (userInfo) => {
  const userPermissionsArray = userInfo?.permissions || [];
  
  // Extract permission names from the nested structure
  const userPermissions = userPermissionsArray.map(item => 
    item?.user_permission?.permission || item?.permission
  ).filter(Boolean);
  
  // Check for permissions (check both boolean flag and permissions array)
  const hasCommissionPermission = userInfo?.hasCommissionPermission || userPermissions.includes('Commission');
  const hasAdminPermission = userPermissions.includes('Admin');
  const hasContracting = userPermissions.includes('Contracting');
  const hasInternalStaff = userPermissions.includes('Internal Staff');
  const hasMasterAdmin = userPermissions.includes('Master Admin');
  const hasDebtAnalyst = userPermissions.includes('Debt Analyst');
  
  // Build Admin Settings subcategory items
  const adminSettingsItems = [];
  if (hasContracting) {
    adminSettingsItems.push({ id: 'admin-contracting', label: 'Contracting', icon: FileSignature });
  }
  if (hasInternalStaff) {
    adminSettingsItems.push({ id: 'admin-user-management', label: 'User Management', icon: UserCog });
  }

  // Build Internal Management subcategories and items
  const internalManagementSubcategories = [];
  
  // Support items (show if user has Internal Staff permission) - placed at top
  if (hasInternalStaff) {
    internalManagementSubcategories.push(
      { id: 'support-tickets', label: 'Support Tickets', icon: HeadphonesIcon },
      { id: 'idea-summary', label: 'Idea Summary', icon: Lightbulb }
    );
  }
  
  // Commissions subcategory (show if user has Commission permission)
  if (hasCommissionPermission) {
    const commissionItems = [
      { id: 'commission-tracker', label: 'Tracker & Monitoring', icon: TrendingUp },
      { id: 'commission-summary', label: 'Summary', icon: BarChart3 },
    ];
    
    // Add Debt Monitoring if user has Debt Analyst permission
    if (hasDebtAnalyst) {
      commissionItems.push({ id: 'debt-monitoring', label: 'Debt Monitoring', icon: AlertCircle });
    }
    
    internalManagementSubcategories.push({
      type: 'subcategory',
      label: 'Commissions',
      items: commissionItems
    });
  }

  // Admin Settings subcategory (show if there are items)
  if (adminSettingsItems.length > 0) {
    internalManagementSubcategories.push({
      type: 'subcategory',
      label: 'Admin Settings',
      items: adminSettingsItems
    });
  }

  // Master Settings subcategory (show if user has Master Admin permission)
  if (hasMasterAdmin) {
    internalManagementSubcategories.push({
      type: 'subcategory',
      label: 'Master Settings',
      items: [
        { id: 'master-user-settings', label: 'Permissions', icon: Settings },
        { id: 'master-locations-pit', label: 'Locations PIT', icon: Shield },
      ]
    });
  }
  
  return [
  {
    type: 'item',
    id: 'leaderboard',
    label: 'Leaderboard',
    icon: Trophy
  },
  {
    type: 'category',
    label: 'My Portal',
    items: [
      { id: 'my-policies', label: 'Policies', icon: FileText },
      { id: 'my-splits', label: 'Splits', icon: Split },
      { id: 'my-commissions', label: 'Commissions', icon: DollarSign },
      { id: 'debt-recovery', label: 'Debt Recovery', icon: Wallet },
    ]
  },
  {
    type: 'category',
    label: 'My Team',
    items: [
      { id: 'downlines', label: 'Downlines', icon: Users },
      { id: 'agency', label: 'Agency', icon: Building },
    ]
  },
  {
    type: 'category',
    label: 'Resources',
    items: [
      { id: 'marketing-hub', label: 'Marketing Hub', icon: Megaphone },
      { id: 'tools', label: 'Tools', icon: Settings },
      {
        type: 'subcategory',
        label: 'Support',
        items: [
          { id: 'my-tickets', label: 'My Tickets', icon: Ticket },
          { id: 'ideas', label: 'Ideas', icon: Lightbulb },
        ]
      }
    ]
  },
  // Show Internal Management if at least one subcategory exists
  ...(internalManagementSubcategories.length > 0 ? [
    {
      type: 'category',
      label: 'Internal Management',
      requiresPermission: true,
      items: internalManagementSubcategories
    }
  ] : [])
];
};

export default function Sidebar({ activeSection, setActiveSection, collapsed, setCollapsed, userInfo }) {
  const [expandedCategories, setExpandedCategories] = useState({
    'My Portal': true,
    'My Team': true,
    'Resources': true,
    'Support': true,
    'Internal Management': true,
    'Commissions': true,
    'Admin Settings': true,
    'Master Settings': true
  });

  const navigationStructure = getNavigationStructure(userInfo);

  const toggleCategory = (label) => {
    setExpandedCategories(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const renderItem = (item, depth = 0) => {
    const Icon = item.icon;
    const isActive = activeSection === item.id;
    
    return (
      <button
        key={item.id}
        onClick={() => setActiveSection(item.id)}
        className={cn(
          "w-full flex items-center gap-3.5 px-3.5 py-3 rounded-xl transition-all duration-200 group",
          collapsed ? "justify-center" : "justify-start",
          isActive
            ? "bg-gradient-to-r from-[#f4e5b8] to-[#fffef0] text-[#b8941e] shadow-sm border border-[#d4af37]/20"
            : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-800",
          depth > 0 && !collapsed && "ml-2"
        )}
        style={{ paddingLeft: collapsed ? undefined : `${(depth + 1) * 12 + 8}px` }}
        data-testid={`nav-${item.id}`}
      >
        <Icon className={cn(
          "w-[18px] h-[18px] flex-shrink-0 transition-transform duration-200",
          isActive ? "scale-110" : "group-hover:scale-105"
        )} />
        {!collapsed && <span className="font-medium text-[13px] leading-tight">{item.label}</span>}
      </button>
    );
  };

  const renderSubcategory = (subcategory, depth = 0) => {
    const isExpanded = expandedCategories[subcategory.label];
    
    return (
      <div key={subcategory.label} className="mb-1">
        {!collapsed && (
          <button
            onClick={() => toggleCategory(subcategory.label)}
            className="w-full flex items-center justify-between px-3.5 py-2.5 text-[11px] font-bold text-neutral-500 hover:text-neutral-700 uppercase tracking-wide rounded-lg hover:bg-neutral-50 transition-colors"
            style={{ paddingLeft: `${(depth + 1) * 12 + 8}px` }}
          >
            <span>{subcategory.label}</span>
            {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRightSmall className="w-3.5 h-3.5" />}
          </button>
        )}
        {(isExpanded || collapsed) && (
          <div className="space-y-0.5 mt-1">
            {subcategory.items.map(item => renderItem(item, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderCategory = (category) => {
    const isExpanded = expandedCategories[category.label];
    
    return (
      <div key={category.label} className="space-y-1 mb-2">
        {!collapsed && (
          <button
            onClick={() => toggleCategory(category.label)}
            className="w-full flex items-center justify-between px-3.5 py-2.5 text-[11px] font-bold text-neutral-400 uppercase tracking-widest hover:text-neutral-600 rounded-lg hover:bg-neutral-50 transition-colors"
          >
            <span>{category.label}</span>
            {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRightSmall className="w-3.5 h-3.5" />}
          </button>
        )}
        {(isExpanded || collapsed) && (
          <div className="space-y-0.5">
            {category.items.map(item => {
              if (item.type === 'subcategory') {
                return renderSubcategory(item, 0);
              }
              return renderItem(item, 0);
            })}
          </div>
        )}
      </div>
    );
  };

  const renderNavItem = (navItem) => {
    if (navItem.type === 'item') {
      return renderItem(navItem, 0);
    } else if (navItem.type === 'category') {
      return renderCategory(navItem);
    }
    return null;
  };

  return (
    <div
      className={cn(
        "bg-gradient-to-b from-white to-neutral-50/30 dark:from-neutral-900 dark:to-neutral-950/30 border-r border-neutral-200/80 dark:border-neutral-800/80 transition-all duration-300 ease-in-out relative shadow-sm",
        collapsed ? "w-20" : "w-72"
      )}
      data-testid="sidebar"
    >
      {/* Logo/Brand */}
      <div className="h-16 flex items-center justify-between px-5 border-b border-neutral-200/60 dark:border-neutral-800/60 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm">
        {!collapsed && (
          <h1 className="text-xl font-bold bg-gradient-to-r from-[#d4af37] to-[#b8941e] bg-clip-text text-transparent">
            SimplyWork
          </h1>
        )}
        {collapsed && (
          <span className="text-xl font-bold mx-auto bg-gradient-to-r from-[#d4af37] to-[#b8941e] bg-clip-text text-transparent">
            SW
          </span>
        )}
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 bg-white border border-neutral-200 rounded-full p-1.5 shadow-md hover:shadow-lg transition-all hover:scale-110"
        style={{ color: '#d4af37' }}
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* Menu Items */}
      <nav className="p-3 pb-[10vh] space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        {navigationStructure.map(renderNavItem)}
      </nav>
    </div>
  );
}
